import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Lock, ShieldAlert } from 'lucide-react';
import { DataGrid } from '@/components/DataGrid';
import { Field, QueryState, Select, type Column } from '@/components/live';
import { callRpc } from '@/api/resources';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { canEditRolePermissions } from '@/lib/accessTabs';
import { toast } from '@/lib/toast';

interface PermissionRow { id: string; module_key: string; screen_key: string; action_key: string; is_high_risk: boolean }
interface GrantRow { role_id: string; permission_id: string; allowed: boolean }
interface RoleRow { id: string; role_code: string; name: string; tier: number; status: string }

/** A row of the matrix: one screen, with the state of each action on it. */
type MatrixRow = { id: string; module_key: string; screen_key: string; granted: string } & Record<string, string>;

/**
 * The order an action is usually granted in, not the alphabetical order the database returns.
 * Read comes first because nothing else is any use without it, and `configure` last because it
 * is the one that changes how a screen behaves rather than what it holds.
 */
const ACTIONS: Array<{ key: string; label: string }> = [
  { key: 'read', label: 'Read' },
  { key: 'create', label: 'Create' },
  { key: 'update', label: 'Update' },
  { key: 'submit', label: 'Submit' },
  { key: 'approve', label: 'Approve' },
  { key: 'delete', label: 'Delete' },
  { key: 'restore', label: 'Restore' },
  { key: 'export', label: 'Export' },
  { key: 'reveal', label: 'Reveal' },
  { key: 'share', label: 'Share' },
  { key: 'configure', label: 'Configure' },
];

/**
 * What each role may do on each screen, as a grid of switches (owner, 2026-09-25: "make in
 * datagrid wiht buttons (on and off)").
 *
 * 429 permissions across 39 screens and 11 actions have been in the database since the beginning
 * and nothing in the application could read them, let alone change one: deciding what a role may
 * do meant editing `authz.role_permissions` by hand. This screen is called "Access & scope" and
 * showed users, record scope and geography - everything except the permissions themselves.
 *
 * Every switch calls `api.fn_set_role_permission`, which is where the rules live. Three of them
 * are asked here too, before the button is offered rather than after the click is refused: the
 * grant with tier 0/1, never a tier 0 role, and never a role at or above your own level - without
 * that last one a tier 1 admin could widen a tier 0 role and then be assigned it.
 *
 * WITHDRAWING SETS `allowed` FALSE rather than deleting the row, so the grant that was there
 * before stays in the audit trail. An off switch and a permission nobody has ever touched are
 * the same thing to `fn_has_permission` and different things to an auditor.
 */
export function RolePermissions() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const roles = useRows<RoleRow>('roles', { orderBy: { column: 'tier' } });
  // EXPLICIT LIMIT, because `listRows` defaults to 200 and the catalogue is 429 rows. Without it
  // two thirds of the screens arrive with five or six of their eleven actions and the rest simply
  // missing - and a missing action renders as "--", so the grid would quietly claim a permission
  // does not exist. The same trap on the grants below is worse: a truncated read shows a
  // permission the role HOLDS as Off, and somebody would turn it on again to fix a lie.
  const catalogue = useRows<PermissionRow>('permissions', { limit: 1000, orderBy: { column: 'screen_key' } });
  const [roleId, setRoleId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const roleList = useMemo(() => (roles.data ?? []).filter((role) => role.status !== 'inactive'), [roles.data]);
  const role = roleList.find((entry) => entry.id === roleId) ?? null;
  // Every grant for this role. Filtered server-side: 1,534 rows across six roles is not much, but
  // the matrix only ever shows one role and a narrower read is a narrower thing to get wrong.
  const grants = useRows<GrantRow>('role_permissions', { filters: { role_id: roleId }, limit: 1000 }, Boolean(roleId));

  const mayEdit = canEditRolePermissions(profile, role);
  const refusal = !role ? null
    : role.tier === 0 ? `${role.name} is the platform's own role. Nobody changes what it may do from here.`
      : profile?.tier != null && role.tier <= profile.tier
        ? `${role.name} sits at or above your own level, so you cannot change what it may do. Otherwise you could widen a role and then be given it.`
        : !mayEdit ? 'Changing a permission needs an organisation admin who configures roles.' : null;

  const rows: MatrixRow[] = useMemo(() => {
    const allowedIds = new Set((grants.data ?? []).filter((grant) => grant.allowed).map((grant) => grant.permission_id));
    const byScreen = new Map<string, MatrixRow>();
    for (const permission of catalogue.data ?? []) {
      const row = byScreen.get(permission.screen_key)
        ?? ({ id: permission.screen_key, module_key: permission.module_key, screen_key: permission.screen_key, granted: '' } as MatrixRow);
      // "--" is a pair the catalogue does not have, which is not the same as one that is off.
      row[permission.action_key] = allowedIds.has(permission.id) ? 'on' : 'off';
      row[`risk:${permission.action_key}`] = permission.is_high_risk ? 'yes' : '';
      byScreen.set(permission.screen_key, row);
    }
    for (const row of byScreen.values()) {
      const on = ACTIONS.filter((action) => row[action.key] === 'on').length;
      // Out of the actions the catalogue actually defines for this screen, not out of eleven: a
      // screen that genuinely has fewer should say so rather than imply missing grants.
      const known = ACTIONS.filter((action) => row[action.key]).length;
      row.granted = `${on} / ${known}`;
    }
    return [...byScreen.values()].sort((a, b) => a.screen_key.localeCompare(b.screen_key));
  }, [catalogue.data, grants.data]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => row.screen_key.includes(term) || row.module_key.includes(term));
  }, [rows, search]);

  async function toggle(row: MatrixRow, actionKey: string) {
    if (!role || !mayEdit) return;
    const next = row[actionKey] !== 'on';
    const cell = `${row.screen_key}:${actionKey}`;
    setBusy(cell);
    try {
      await callRpc('fn_set_role_permission', {
        p_role_id: role.id, p_screen_key: row.screen_key, p_action_key: actionKey, p_allowed: next,
      });
      await queryClient.invalidateQueries({ queryKey: ['role_permissions'] });
      toast.success(`${role.name}: ${actionKey} on ${row.screen_key} is ${next ? 'on' : 'off'}. It applies the next time they sign in.`);
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'That could not be changed.');
    } finally {
      setBusy(null);
    }
  }

  const columns: Array<Column<MatrixRow>> = [
    { key: 'screen_key', label: 'Screen', render: (row) => (
      <span className="cell-stack"><strong>{row.screen_key}</strong><small className="muted">{row.module_key}</small></span>
    ) },
    { key: 'granted', label: 'On', align: 'right', render: (row) => <span className="muted">{row.granted}</span> },
    ...ACTIONS.map((action): Column<MatrixRow> => ({
      key: action.key,
      label: action.label,
      // `Column.align` offers only 'right'. The switch is a fixed width instead, so the eleven
      // columns stay in line without inventing an alignment the table does not have.
      render: (row) => {
        const state = row[action.key];
        if (!state) return <span className="muted">--</span>;
        const on = state === 'on';
        const cell = `${row.screen_key}:${action.key}`;
        return (
          <button
            aria-label={`${action.label} on ${row.screen_key}: ${on ? 'on' : 'off'}`}
            aria-pressed={on}
            className="onoff-toggle"
            disabled={!mayEdit || busy !== null}
            onClick={() => void toggle(row, action.key)}
            title={!mayEdit ? (refusal ?? 'Viewing only.')
              : row[`risk:${action.key}`]
                ? `${action.label} on ${row.screen_key} is a high-risk permission.`
                : `Turn ${action.label.toLowerCase()} ${on ? 'off' : 'on'} for ${row.screen_key}`}
            type="button"
          >
            {busy === cell ? '...' : on ? 'On' : 'Off'}
            {row[`risk:${action.key}`] ? <ShieldAlert aria-hidden size={11} /> : null}
          </button>
        );
      },
    })),
  ];

  return (
    <>
      <div className="field-row">
        <Field
          hint="You cannot change a role at or above your own level, and never the platform's Super Admin."
          label="Role"
          required
        >
          <Select
            onChange={setRoleId}
            options={roleList.map((entry) => ({ id: entry.id, label: `${entry.name} (tier ${entry.tier})` }))}
            placeholder="Choose a role to see what it may do"
            value={roleId}
          />
        </Field>
      </div>

      {role && refusal && (
        <p className="offline-banner"><Lock size={13} /> {refusal}</p>
      )}

      {!role ? (
        <p className="state-note">Choose a role. Every screen it can reach, and every action on it, is listed here.</p>
      ) : (
        <QueryState empty="No permissions are defined." query={catalogue}>
          <DataGrid
            columns={columns}
            emptyMessage={search ? `No screen matches "${search}".` : 'No permissions are defined.'}
            exportName={`permissions-${role.role_code.toLowerCase()}`}
            onSearchChange={setSearch}
            pageSize={50}
            rows={visible}
            screenKey="role.configuration"
            search={search}
            searchPlaceholder="Search a screen or module"
          />
        </QueryState>
      )}
    </>
  );
}
